import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Shield, User, ChevronLeft, ShieldX, ShieldPlus, Pencil, Trash2, UserPlus, Search, Users, Mail, ChevronDown, ChevronUp, Phone, Briefcase, Building2, Globe, Calendar, RefreshCw, Clock } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: string;
  created_at: string;
  avatar_url: string | null;
  position: string | null;
  phone: string | null;
  business_name: string | null;
  business_service: string | null;
  business_website: string | null;
  business_phone: string | null;
  business_logo_url: string | null;
}

interface UserStats {
  totalUsers: number;
  totalAdmins: number;
  newThisMonth: number;
}

interface UserStatus {
  email_confirmed_at: string | null;
  last_sign_in_at: string | null;
  confirmed_at: string | null;
  created_at: string | null;
  has_set_password: boolean;
}

export default function AdminUsers() {
  const { profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserProfile[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState<UserStats>({ totalUsers: 0, totalAdmins: 0, newThisMonth: 0 });
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [userStatuses, setUserStatuses] = useState<Record<string, UserStatus>>({});
  const [loadingStatuses, setLoadingStatuses] = useState(false);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);

  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    role: 'crew' as 'crew' | 'admin',
  });

  useEffect(() => {
    if (authLoading) return;

    // Wait for profile to be loaded before checking role
    if (!profile) return;

    if (profile.role !== 'admin') {
      navigate('/');
      return;
    }
    loadUsers();
  }, [authLoading, profile, navigate]);

  useEffect(() => {
    if (users.length > 0) {
      loadUserStatuses();
    }
  }, [users]);

  useEffect(() => {
    const filtered = users.filter(user =>
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.full_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  const loadUsers = async () => {
    setUsersLoading(true);
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (data) {
      setUsers(data);
      setFilteredUsers(data);

      const totalAdmins = data.filter(u => u.role === 'admin').length;
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const newThisMonth = data.filter(u => new Date(u.created_at) > thirtyDaysAgo).length;

      setStats({
        totalUsers: data.length,
        totalAdmins,
        newThisMonth,
      });
    }
    setUsersLoading(false);
  };

  const loadUserStatuses = async () => {
    setLoadingStatuses(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-user-status`;
      const statusesMap: Record<string, UserStatus> = {};

      await Promise.all(
        users.map(async (user) => {
          try {
            const response = await fetch(apiUrl, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ user_id: user.id }),
            });

            const result = await response.json();
            if (result.success && result.status) {
              statusesMap[user.id] = result.status;
            }
          } catch (error) {
            console.error(`Failed to fetch status for user ${user.id}:`, error);
          }
        })
      );

      setUserStatuses(statusesMap);
    } catch (error) {
      console.error('Failed to load user statuses:', error);
    } finally {
      setLoadingStatuses(false);
    }
  };

  const toggleAdminStatus = async (userId: string, currentRole: string) => {
    if (userId === profile?.id) {
      alert("You cannot change your own admin status");
      return;
    }

    setUpdating(userId);
    const newRole = currentRole === 'admin' ? 'crew' : 'admin';

    const { error } = await supabase
      .from('profiles')
      .update({ role: newRole })
      .eq('id', userId);

    if (error) {
      alert('Failed to update user role');
      console.error(error);
    } else {
      await loadUsers();
    }
    setUpdating(null);
  };

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setUpdating('invite');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('You must be logged in to invite users');
        setUpdating(null);
        return;
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-user`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to send invitation');
      }

      alert(`Invitation sent to ${formData.email}!\n\nThe user will receive an email with a link to set their password. Their account has been created and is ready to use once they complete setup.`);
      await loadUsers();
      setShowInviteModal(false);
      setFormData({ email: '', full_name: '', role: 'crew' });
    } catch (error: any) {
      alert('Failed to send invitation: ' + error.message);
    } finally {
      setUpdating(null);
    }
  };

  const handleResendInvite = async (user: UserProfile) => {
    setUpdating(user.id);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('You must be logged in to resend invitations');
        setUpdating(null);
        return;
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-user`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          full_name: user.full_name,
          resend: true,
          role: user.role,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to resend invitation');
      }

      alert(`Invitation email resent to ${user.email}!\n\nA new invitation link has been sent. Previous invitation links for this user are no longer valid.`);
    } catch (error: any) {
      alert('Failed to resend invitation: ' + error.message);
    } finally {
      setUpdating(null);
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    setUpdating(selectedUser.id);

    const { error } = await supabase
      .from('profiles')
      .update({
        full_name: formData.full_name,
        email: formData.email,
        role: formData.role,
      })
      .eq('id', selectedUser.id);

    if (error) {
      alert('Failed to update user: ' + error.message);
    } else {
      await loadUsers();
      setShowEditModal(false);
      setSelectedUser(null);
    }
    setUpdating(null);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setUpdating(selectedUser.id);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('You must be logged in to delete users');
        setUpdating(null);
        return;
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: selectedUser.id,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to delete user');
      }

      await loadUsers();
      setShowDeleteDialog(false);
      setSelectedUser(null);
    } catch (error: any) {
      alert('Failed to delete user: ' + error.message);
    } finally {
      setUpdating(null);
    }
  };

  const openEditModal = (user: UserProfile) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      full_name: user.full_name,
      role: user.role as 'crew' | 'admin',
    });
    setShowEditModal(true);
  };

  const openDeleteDialog = (user: UserProfile) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  if (authLoading || usersLoading) {
    return (
      <div className="min-h-screen bg-zinc-900 text-white flex items-center justify-center">
        <div className="text-zinc-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-4"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Dashboard
          </button>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary-500 rounded flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">User Management</h1>
                <p className="text-sm sm:text-base text-zinc-400">Manage users and admin access</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button
                onClick={loadUserStatuses}
                disabled={loadingStatuses}
                className="bg-zinc-700 hover:bg-zinc-600 text-white border-zinc-600 justify-center"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingStatuses ? 'animate-spin' : ''}`} />
                {loadingStatuses ? 'Refreshing...' : 'Refresh Status'}
              </Button>
              <Button
                onClick={() => setShowInviteModal(true)}
                className="bg-primary-500 hover:bg-primary-600 text-white border-primary-500 justify-center"
              >
                <Mail className="w-4 h-4 mr-2" />
                Invite User
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">Total Users</p>
                <p className="text-3xl font-bold mt-1">{stats.totalUsers}</p>
              </div>
              <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-primary-400" />
              </div>
            </div>
          </div>

          <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">Administrators</p>
                <p className="text-3xl font-bold mt-1">{stats.totalAdmins}</p>
              </div>
              <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary-400" />
              </div>
            </div>
          </div>

          <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">New This Month</p>
                <p className="text-3xl font-bold mt-1">{stats.newThisMonth}</p>
              </div>
              <div className="w-12 h-12 bg-green-600/20 rounded-lg flex items-center justify-center">
                <UserPlus className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-zinc-800 rounded-2xl border border-zinc-700 overflow-hidden">
          <div className="p-6 border-b border-zinc-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
              <Input
                type="text"
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-zinc-700 border-zinc-600 text-white placeholder:text-zinc-400"
              />
            </div>
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-700 bg-zinc-800/50">
                  <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">User</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Email</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Role</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Joined</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <>
                    <tr
                      key={user.id}
                      className="border-b border-zinc-700/50 hover:bg-zinc-700/30 transition-colors cursor-pointer"
                      onClick={() => setExpandedUserId(expandedUserId === user.id ? null : user.id)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8">
                            {expandedUserId === user.id ? (
                              <ChevronUp className="w-5 h-5 text-zinc-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-zinc-400" />
                            )}
                          </div>
                          {user.avatar_url ? (
                            <img
                              src={user.avatar_url}
                              alt={user.full_name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-zinc-700 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-zinc-400" />
                            </div>
                          )}
                          <div>
                            <div className="font-medium">{user.full_name}</div>
                            {user.id === profile?.id && (
                              <div className="text-xs text-zinc-500">(You)</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-zinc-400">{user.email}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                          user.role === 'admin'
                            ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                            : 'bg-zinc-700 text-zinc-300'
                        }`}>
                          {user.role === 'admin' && <Shield className="w-3 h-3" />}
                          {user.role === 'admin' ? 'Admin' : 'User'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {loadingStatuses ? (
                          <span className="text-zinc-500 text-sm">Loading...</span>
                        ) : userStatuses[user.id] ? (
                          <div className="flex flex-col gap-1">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                              userStatuses[user.id].has_set_password
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                            }`}>
                              <div className={`w-1.5 h-1.5 rounded-full ${
                                userStatuses[user.id].has_set_password ? 'bg-green-400' : 'bg-yellow-400'
                              }`} />
                              {userStatuses[user.id].has_set_password ? 'Active' : 'Invited'}
                            </span>
                            {userStatuses[user.id].last_sign_in_at && (
                              <span className="text-xs text-zinc-500 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(userStatuses[user.id].last_sign_in_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-zinc-500 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-zinc-400">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-start gap-2">
                          {user.id === profile?.id ? (
                            <span className="text-xs text-zinc-500">Cannot modify self</span>
                          ) : (
                            <>
                              <button
                                onClick={() => toggleAdminStatus(user.id, user.role)}
                                disabled={updating === user.id}
                                className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                                  user.role === 'admin'
                                    ? 'text-red-400 hover:text-red-300 hover:bg-red-500/10'
                                    : 'text-primary-400 hover:text-primary-300 hover:bg-primary-500/10'
                                }`}
                                title={user.role === 'admin' ? 'Remove admin access' : 'Grant admin access'}
                              >
                                {user.role === 'admin' ? (
                                  <ShieldX className="w-4 h-4" />
                                ) : (
                                  <ShieldPlus className="w-4 h-4" />
                                )}
                              </button>
                              {userStatuses[user.id] && !userStatuses[user.id].has_set_password && (
                                <button
                                  onClick={() => handleResendInvite(user)}
                                  disabled={updating === user.id}
                                  className="p-2 text-zinc-400 hover:text-primary-400 hover:bg-primary-500/10 rounded-lg transition-colors disabled:opacity-50"
                                  title="Resend invitation email"
                                >
                                  <Mail className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => openEditModal(user)}
                                className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
                                title="Edit user"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openDeleteDialog(user)}
                                className="p-2 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                title="Delete user"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedUserId === user.id && (
                      <tr key={`${user.id}-details`} className="border-b border-zinc-700/50 bg-zinc-800/50">
                        <td colSpan={6} className="px-6 py-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                <User className="w-5 h-5 text-primary-400" />
                                Personal Information
                              </h3>
                              <div className="space-y-3">
                                <div className="flex items-start gap-3">
                                  <Mail className="w-4 h-4 text-zinc-400 mt-1" />
                                  <div>
                                    <div className="text-xs text-zinc-500">Email</div>
                                    <div className="text-sm">{user.email}</div>
                                  </div>
                                </div>
                                {user.position && (
                                  <div className="flex items-start gap-3">
                                    <Briefcase className="w-4 h-4 text-zinc-400 mt-1" />
                                    <div>
                                      <div className="text-xs text-zinc-500">Position</div>
                                      <div className="text-sm">{user.position}</div>
                                    </div>
                                  </div>
                                )}
                                {user.phone && (
                                  <div className="flex items-start gap-3">
                                    <Phone className="w-4 h-4 text-zinc-400 mt-1" />
                                    <div>
                                      <div className="text-xs text-zinc-500">Phone</div>
                                      <div className="text-sm">{user.phone}</div>
                                    </div>
                                  </div>
                                )}
                                <div className="flex items-start gap-3">
                                  <Calendar className="w-4 h-4 text-zinc-400 mt-1" />
                                  <div>
                                    <div className="text-xs text-zinc-500">Joined</div>
                                    <div className="text-sm">{new Date(user.created_at).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric'
                                    })}</div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div>
                              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                <Building2 className="w-5 h-5 text-primary-400" />
                                Business Information
                              </h3>
                              {user.business_name || user.business_service || user.business_website || user.business_phone ? (
                                <div className="space-y-3">
                                  {user.business_name && (
                                    <div className="flex items-start gap-3">
                                      <Building2 className="w-4 h-4 text-zinc-400 mt-1" />
                                      <div>
                                        <div className="text-xs text-zinc-500">Business Name</div>
                                        <div className="text-sm">{user.business_name}</div>
                                      </div>
                                    </div>
                                  )}
                                  {user.business_service && (
                                    <div className="flex items-start gap-3">
                                      <Briefcase className="w-4 h-4 text-zinc-400 mt-1" />
                                      <div>
                                        <div className="text-xs text-zinc-500">Service Offering</div>
                                        <div className="text-sm">{user.business_service}</div>
                                      </div>
                                    </div>
                                  )}
                                  {user.business_website && (
                                    <div className="flex items-start gap-3">
                                      <Globe className="w-4 h-4 text-zinc-400 mt-1" />
                                      <div>
                                        <div className="text-xs text-zinc-500">Website</div>
                                        <a
                                          href={user.business_website}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {user.business_website}
                                        </a>
                                      </div>
                                    </div>
                                  )}
                                  {user.business_phone && (
                                    <div className="flex items-start gap-3">
                                      <Phone className="w-4 h-4 text-zinc-400 mt-1" />
                                      <div>
                                        <div className="text-xs text-zinc-500">Business Phone</div>
                                        <div className="text-sm">{user.business_phone}</div>
                                      </div>
                                    </div>
                                  )}
                                  {user.business_logo_url && (
                                    <div className="flex items-start gap-3">
                                      <div className="w-4 h-4 mt-1" />
                                      <div>
                                        <div className="text-xs text-zinc-500 mb-2">Business Logo</div>
                                        <img
                                          src={user.business_logo_url}
                                          alt="Business logo"
                                          className="h-12 object-contain bg-white rounded p-1"
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-sm text-zinc-500">No business information provided</div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden divide-y divide-zinc-700">
            {filteredUsers.map((user) => (
              <div key={user.id} className="p-4">
                <div className="flex items-start gap-3 mb-3">
                  {user.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={user.full_name}
                      className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-zinc-700 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-zinc-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-white">{user.full_name}</h3>
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                        user.role === 'admin'
                          ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                          : 'bg-zinc-700 text-zinc-300'
                      }`}>
                        {user.role === 'admin' && <Shield className="w-3 h-3" />}
                        {user.role === 'admin' ? 'Admin' : 'User'}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-400 truncate">{user.email}</p>
                    {user.id === profile?.id && (
                      <p className="text-xs text-zinc-500 mt-1">(You)</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2 mb-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-500">Status:</span>
                    {loadingStatuses ? (
                      <span className="text-zinc-500">Loading...</span>
                    ) : userStatuses[user.id] ? (
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        userStatuses[user.id].has_set_password
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                      }`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${
                          userStatuses[user.id].has_set_password ? 'bg-green-400' : 'bg-yellow-400'
                        }`} />
                        {userStatuses[user.id].has_set_password ? 'Active' : 'Invited'}
                      </span>
                    ) : (
                      <span className="text-zinc-500">-</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-500">Joined:</span>
                    <span className="text-zinc-400">{new Date(user.created_at).toLocaleDateString()}</span>
                  </div>
                  {userStatuses[user.id]?.last_sign_in_at && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-500">Last sign in:</span>
                      <span className="text-zinc-400 text-xs flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(userStatuses[user.id].last_sign_in_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  )}
                </div>

                {user.id !== profile?.id && (
                  <div className="flex flex-col gap-2 pt-3 border-t border-zinc-700">
                    <button
                      onClick={() => toggleAdminStatus(user.id, user.role)}
                      disabled={updating === user.id}
                      className={`w-full inline-flex items-center justify-center px-3 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                        user.role === 'admin'
                          ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30'
                          : 'bg-primary-500/20 text-primary-400 hover:bg-primary-500/30 border border-primary-500/30'
                      }`}
                      title={user.role === 'admin' ? 'Remove admin access' : 'Grant admin access'}
                    >
                      {user.role === 'admin' ? (
                        <ShieldX className="w-5 h-5" />
                      ) : (
                        <ShieldPlus className="w-5 h-5" />
                      )}
                    </button>
                    {userStatuses[user.id] && !userStatuses[user.id].has_set_password && (
                      <button
                        onClick={() => handleResendInvite(user)}
                        disabled={updating === user.id}
                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-primary-500/20 hover:bg-primary-500/30 text-primary-400 border border-primary-500/30 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                      >
                        <Mail className="w-4 h-4" />
                        {updating === user.id ? 'Resending...' : 'Resend Invite'}
                      </button>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(user)}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => openDeleteDialog(user)}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg text-sm font-medium transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                    <button
                      onClick={() => setExpandedUserId(expandedUserId === user.id ? null : user.id)}
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-zinc-700/50 hover:bg-zinc-700 text-zinc-300 rounded-lg text-sm font-medium transition-colors"
                    >
                      {expandedUserId === user.id ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Hide Details
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          Show Details
                        </>
                      )}
                    </button>
                  </div>
                )}

                {expandedUserId === user.id && (
                  <div className="mt-4 pt-4 border-t border-zinc-700 space-y-4">
                    <div>
                      <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                        <User className="w-4 h-4 text-primary-400" />
                        Personal Information
                      </h4>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <Mail className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-xs text-zinc-500">Email</div>
                            <div className="text-sm text-zinc-300 break-all">{user.email}</div>
                          </div>
                        </div>
                        {user.position && (
                          <div className="flex items-start gap-2">
                            <Briefcase className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <div className="text-xs text-zinc-500">Position</div>
                              <div className="text-sm text-zinc-300">{user.position}</div>
                            </div>
                          </div>
                        )}
                        {user.phone && (
                          <div className="flex items-start gap-2">
                            <Phone className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <div className="text-xs text-zinc-500">Phone</div>
                              <div className="text-sm text-zinc-300">{user.phone}</div>
                            </div>
                          </div>
                        )}
                        <div className="flex items-start gap-2">
                          <Calendar className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="text-xs text-zinc-500">Joined</div>
                            <div className="text-sm text-zinc-300">{new Date(user.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-primary-400" />
                        Business Information
                      </h4>
                      {user.business_name || user.business_service || user.business_website || user.business_phone ? (
                        <div className="space-y-2">
                          {user.business_name && (
                            <div className="flex items-start gap-2">
                              <Building2 className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                              <div>
                                <div className="text-xs text-zinc-500">Business Name</div>
                                <div className="text-sm text-zinc-300">{user.business_name}</div>
                              </div>
                            </div>
                          )}
                          {user.business_service && (
                            <div className="flex items-start gap-2">
                              <Briefcase className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                              <div>
                                <div className="text-xs text-zinc-500">Service Offering</div>
                                <div className="text-sm text-zinc-300">{user.business_service}</div>
                              </div>
                            </div>
                          )}
                          {user.business_website && (
                            <div className="flex items-start gap-2">
                              <Globe className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                              <div className="min-w-0">
                                <div className="text-xs text-zinc-500">Website</div>
                                <a
                                  href={user.business_website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-primary-400 hover:text-primary-300 transition-colors break-all"
                                >
                                  {user.business_website}
                                </a>
                              </div>
                            </div>
                          )}
                          {user.business_phone && (
                            <div className="flex items-start gap-2">
                              <Phone className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                              <div>
                                <div className="text-xs text-zinc-500">Business Phone</div>
                                <div className="text-sm text-zinc-300">{user.business_phone}</div>
                              </div>
                            </div>
                          )}
                          {user.business_logo_url && (
                            <div>
                              <div className="text-xs text-zinc-500 mb-2">Business Logo</div>
                              <img
                                src={user.business_logo_url}
                                alt="Business logo"
                                className="h-12 object-contain bg-white rounded p-1"
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-zinc-500">No business information provided</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 bg-primary-500/10 border border-primary-500/30 rounded-xl p-3 sm:p-4">
          <div className="flex gap-2 sm:gap-3">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-primary-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs sm:text-sm text-primary-200">
              <strong className="font-medium">Admin Privileges:</strong> Admins can view and manage all events,
              access the dashboard, manage users, and grant admin access to other users.
            </div>
          </div>
        </div>
      </div>

      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-800 rounded-2xl border border-zinc-700 p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-primary-500/20 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Invite User</h2>
                <p className="text-sm text-zinc-400">Send an invitation email to join</p>
              </div>
            </div>
            <form onSubmit={handleInviteUser} className="space-y-4">
              <div>
                <Label htmlFor="invite-name">Full Name</Label>
                <Input
                  id="invite-name"
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="mt-1 bg-zinc-700 border-zinc-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="invite-email">Email</Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 bg-zinc-700 border-zinc-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="invite-role">Role</Label>
                <select
                  id="invite-role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as 'crew' | 'admin' })}
                  className="mt-1 w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                >
                  <option value="crew">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="bg-primary-500/10 border border-primary-500/30 rounded-lg p-3">
                <p className="text-sm text-primary-200">
                  <strong>How it works:</strong> The account will be created immediately and an invitation email will be sent. The user clicks the link, sets their password, and can start using the platform right away. No email confirmation required.
                </p>
              </div>
              <div className="flex gap-3 mt-6">
                <Button
                  type="button"
                  onClick={() => {
                    setShowInviteModal(false);
                    setFormData({ email: '', full_name: '', role: 'crew' });
                  }}
                  className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white"
                  disabled={updating === 'invite'}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-primary-500 hover:bg-primary-600 text-white"
                  disabled={updating === 'invite'}
                >
                  {updating === 'invite' ? 'Sending...' : 'Send Invitation'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-800 rounded-2xl border border-zinc-700 p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold mb-6">Edit User</h2>
            <form onSubmit={handleEditUser} className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Full Name</Label>
                <Input
                  id="edit-name"
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="mt-1 bg-zinc-700 border-zinc-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 bg-zinc-700 border-zinc-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-role">Role</Label>
                <select
                  id="edit-role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as 'crew' | 'admin' })}
                  className="mt-1 w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white"
                >
                  <option value="crew">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex gap-3 mt-6">
                <Button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                    setFormData({ email: '', full_name: '', role: 'crew' });
                  }}
                  className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white"
                  disabled={updating === selectedUser.id}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-primary-500 hover:bg-primary-600 text-white"
                  disabled={updating === selectedUser.id}
                >
                  {updating === selectedUser.id ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <AlertDialog open={showDeleteDialog} onOpenChange={(open) => {
        setShowDeleteDialog(open);
        if (!open) setSelectedUser(null);
      }}>
        <AlertDialogContent className="bg-zinc-800 border-zinc-700 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete {selectedUser?.full_name}? This will permanently delete the user
              and all their associated data, including events they created. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowDeleteDialog(false);
                setSelectedUser(null);
              }}
              className="bg-zinc-700 hover:bg-zinc-600 text-white border-zinc-600"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={!!updating}
            >
              {updating ? 'Deleting...' : 'Delete User'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
